import * as THREE from "three";
import { Vector3, Color, Object3D } from "three";
import deviceHelper from "./deviceHelper";

const isMobile = deviceHelper.isMobileDevice();
class SceneHelper {

  constructor() {
    this.sprite = null;
    this.apartmentSprite = null;
    this.apartmentBackgroundSprite = null;
    this.apartmentSprite = null;
    this.hotspotScene = null;
    this.sphere = null;
    // this.item = null;
  }

  /**
   * Creates camera with settings.
   */
  initializeCamera() {
    let camera = new THREE.PerspectiveCamera(
      100,
      // window.innerWidth / window.innerHeight,
      1.777777,
      0.1,
      2000
    );
    camera.setFocalLength(35)
    // camera.lookAt(new Vector3(0.10593, -2.0524, 0));

    // This is where the camera position is hardcoded to align the hotspots
    // camera.position.copy(
    //   new Vector3(22.612559451406984, 30.53181744066928, 45.523622716900114)
    //   );
    camera.position.copy(
      new Vector3(3800, 3700, 1)
    );



    camera.lookAt(new Vector3(0, 0, 0));
    camera.updateProjectionMatrix();
    return camera;
  }


  /**
   * Creates renderer with settings.
   */
  initializeRenderer(canvasName) {
    let renderer = new THREE.WebGLRenderer({
      antialias: true,
      canvas: document.getElementById(canvasName),
      alpha: true,
    });

    renderer.setPixelRatio(Math.min(2, window.devicePixelRatio) || 1);
    renderer.physicallyCorrectLights = true;
    renderer.shadowMap.enabled = false;
    renderer.autoClearColor = false;
    // renderer.sortObjects = false;
    renderer.autoClear = true;
    renderer.autoClearDepth = true;
    renderer.autoClearStencil = true;
    renderer.debug.checkShaderErrors = false;
    renderer.maxMorphNormals = 0;
    renderer.maxMorphTargets = 0;
    renderer.capabilities = {
      ...renderer.capabilities,
      floatFragmentTextures: false,
      floatVertexTextures: false,
      isWebGL2: true,
      logarithmicDepthBuffer: false,
      maxAttributes: 0,
      maxCubemapSize: 0,
      maxFragmentUniforms: 0,
      maxTextureSize: 0,
      maxTextures: 0,
      maxVaryings: 0,
      maxVertexTextures: 0,
      maxVertexUniforms: 0,
      precision: null,
      vertexTextures: false,
    };

    // renderer.autoClear = false;
    isMobile ? renderer.setSize(window.innerWidth, window.innerHeight) : renderer.setSize(window.innerWidth * 0.9, (window.innerWidth * 0.9) * 0.5625);

    renderer.setClearColor(new Color("white"));

    return renderer;
  }




  /**
   * Creates a sphere with a texture applied to the inside.
   * @param imageUrl Url to the image for the texture
   */
  initializeBackground(imageUrl) {
    if (!imageUrl) return new Object3D()

    const skydomeTexture = new THREE.TextureLoader().load(imageUrl);
    skydomeTexture.minFilter = THREE.LinearFilter;
    const skyDomeMaterial = new THREE.MeshBasicMaterial({
      map: skydomeTexture,
    });

    const sphere = new THREE.Mesh(
      new THREE.SphereGeometry(1000, 32, 32),
      skyDomeMaterial
    );
    sphere.material.side = THREE.DoubleSide;

    this.sphere = sphere;
    return sphere;
  }

  /**
   * Creates sprite.
   */
  initializeSprite() {
    if (this.sprite) return this.sprite;

    let sprite = new THREE.Sprite(new THREE.SpriteMaterial());

    return (this.sprite = sprite);
  }

  /**
   * Creates apartment sprite.
   */
  initializeApartmentSprite() {
    if (this.apartmentSprite) return this.apartmentSprite;

    let apartmentSprite = new THREE.Sprite(new THREE.SpriteMaterial());
    return (this.apartmentSprite = apartmentSprite);
  }


  /**
   * Changes apartment sprite texture.
   * @param texture Texture that to be applied.
   */
  updateApartmentTexture(texture) {

    texture.minFilter = THREE.LinearFilter;

    const sprite = this.initializeApartmentSprite();
    const textureAspectRation = texture.image.width / texture.image.height;

    sprite.scale.set(6 * textureAspectRation, 5.5, 1);
    sprite.material.map = texture;
    sprite.material.needsUpdate = true;
  }

  /**
   * Changes the sprite texture.
   * @param texture Texture that to be applied.
   */
  updateTexture(texture) {
    const sprite = this.initializeSprite();
    const textureAspectRation = texture.image.width / texture.image.height;

    const multiplier = 3;
    sprite.scale.set(17.83 * textureAspectRation * multiplier, 17.83 * multiplier, 1);

    sprite.material.map = texture;
    sprite.material.needsUpdate = true;
  }

  /**
   * Updates the camera position to the appropriate frame of the camera path depending on the current column.
   * @param camera Three.js main canvas camera
   * @param cameraKeyframeTrack Keyframe position track of the camera animation
   * @param currentColumn Current column being shown on the main scene
   * @param distanceScaleVariable Variable used for scaling distances
   */
  updateCameraPosition(camera, cameraKeyframeTrack, currentColumn, distanceScaleVariable) {
    if (!cameraKeyframeTrack) {
      camera.position.copy(
        new Vector3(38, 37, 1)
      );
      // camera.position.copy(
      // new Vector3(-72.2016, 91.4999, 112.908)
      // );
      return camera.position
    }

    const position = this.createPositionFromAnimation(cameraKeyframeTrack, currentColumn, distanceScaleVariable)
    camera.position.copy(position);
    return position
  }

  /**
   * Updates the camera target position to the appropriate frame of the camera target path depending on the current column.
   * @param camera Three.js main canvas camera
   * @param cameraTargetKeyframeTrack Keyframe position track of the camera animation
   * @param currentColumn Current column being shown on the main scene
   * @param targetDefaultPosition Default position of the target in the provided fbx
   * @param distanceScaleVariable Variable used for scaling distances
   */
  updateTargetPosition(camera, cameraTargetKeyframeTrack, currentColumn, targetDefaultPosition, distanceScaleVariable) {
    if (cameraTargetKeyframeTrack === undefined) {
      if (targetDefaultPosition === undefined) {
        camera.lookAt(new Vector3(0, 0, 0));
        return new Vector3(0, 0, 0);
      }
      camera.lookAt(targetDefaultPosition.clone().multiplyScalar(distanceScaleVariable));
      return targetDefaultPosition.clone().multiplyScalar(distanceScaleVariable);
    }
    const position = this.createPositionFromAnimation(cameraTargetKeyframeTrack, currentColumn, distanceScaleVariable)
    camera.lookAt(position);
    return position
  }

  /**
   * Updates the sprite position to the appropriate frame of the camera target path depending on the current column.
   * @param scene Main scene of the main canvas where the sprite added
   * @param cameraTargetKeyframeTrack Keyframe position track of the camera animation
   * @param currentColumn Current column being shown on the main scene
   * @param defaultDistance Default distance of the camera and camera target
   * @param newDistance Current distance of the camera and camera target
   * @param distanceScaleVariable Variable used for scaling distances
   * @param targetDefaultPosition Default position of the target in the provided fbx
   */
  updateSpritePosition(sprite, cameraTargetKeyframeTrack, currentColumn, defaultDistance, newDistance, distanceScaleVariable, targetPosition) {
    if (cameraTargetKeyframeTrack === undefined) {
      this.compensateForSizeAttenuation(sprite, defaultDistance, newDistance)
      sprite.position.copy(targetPosition);
      return
    }
    this.compensateForSizeAttenuation(sprite, defaultDistance, newDistance)
    const position = this.createPositionFromAnimation(cameraTargetKeyframeTrack, currentColumn, distanceScaleVariable)
    sprite.position.copy(position)
  }

  /**
   * Updates the size of the sprite to  compensate for the size change of increasing/decreasing distance.
   * @param sprite Sprite of the main scene for displaying renders
   * @param defaultDistance Default distance of the camera and camera target
   * @param newDistance Current distance of the camera and camera target
   */
  compensateForSizeAttenuation(sprite, defaultDistance, newDistance) {
    const delta = defaultDistance - newDistance;
    const percentageDifference = -delta / defaultDistance;
    let multiplier = 1;
    multiplier = 1 + percentageDifference;
    sprite.scale.set(17.83 * 1.7777777777777777 * multiplier * 3, 17.83 * multiplier * 3, 1)
  }

  /**
   * Returns a vector scaled to the division variable from the passed animation depending on the current column.
   * @param keyframeTrack Keyframe position track
   * @param currentColumn Current column being shown on the main scene
   * @param distanceScaleVariable Variable used for scaling distances
   */
  createPositionFromAnimation(keyframeTrack, currentColumn, distanceScaleVariable) {
    const positionX = keyframeTrack.values[currentColumn * 3]
    const positionY = keyframeTrack.values[(currentColumn * 3) + 1]
    const positionZ = keyframeTrack.values[(currentColumn * 3) + 2]
    return new Vector3(positionX * distanceScaleVariable, positionY * distanceScaleVariable, positionZ * distanceScaleVariable)
  }

  loadCameraAndTargetAnimations(loader, fbxUrl) {
    return new Promise((resolve, reject) => {
      loader.load(
        fbxUrl,
        (object) => {
          let cameraAnimation = undefined;
          let targetAnimation = undefined;

          object.animations.forEach(animation => {

            animation.tracks.forEach(track => {
              if (track.name.toLowerCase().includes("target") && track.name.includes("position")) {
                targetAnimation = track;
                return
              }
              if (track.name.includes("position")) {
                cameraAnimation = track;
                return
              }
            })

          })

          object.traverse(child => {
            if (child.name.toLowerCase().includes("target")) {
              const event = new CustomEvent("setTargetDefaultPosition", {
                detail: {
                  position: child.position,
                }
              })
              window.dispatchEvent(event)
            }
          })
          resolve([cameraAnimation, targetAnimation])
        },
        (data) => null,
        (error) => reject(error)
      )
    });

  }

  /**
   * Calls a loader method for each fbx from the API.
   * Waits for all files to finish loading.
   * @param fbxLoader Loader for fbx files
   * @param hotspotScene Scene for hotspots
   * @param camera Camera for rendering in three.js
   * @param hotspotArray Parameters that tell the function which files to load
   * @param scene Scene for three.js
   * @param props Props from the ObjectWindow
   * @param gui Temporary GUI for testing
   */
  loadHotspots(fbxLoader, hotspotScene, camera, hotspotArray, scene, props) {
    const hotspotDictionary = {}
    const promises = [];

    hotspotArray.forEach(hotspot => {
      promises.push(
        new Promise(
          resolve => {
            resolve(this.loadHotspot(hotspot.hotspotUrl, fbxLoader, camera, props))
          }
        ));
    });

    return Promise.all(promises)
      .then(hotSpotObjects => {
        hotspotArray.forEach((hotspot, index) => {
          hotspotDictionary[index] = hotSpotObjects[index];
        });

        return hotspotDictionary;
      })
      .catch(console.error)
  }



  /**
   * Loads a hotspot from a passed url.
   * @param hotspotUrl Url for a fbx file containing hotspots and scene info 
   * @param fbxLoader Three js fbx loader
   */
  loadHotspot(hotspotUrl, fbxLoader, camera, props) {
    return new Promise((resolve, reject) => {
      if (hotspotUrl === "") {
        const emptyObject = new THREE.Object3D()
        resolve(emptyObject)
      }
      fbxLoader.load(
        hotspotUrl,

        (fbxScene) => {
          fbxScene.visible = true;
          fbxScene.traverse(function (item) {
            if (item.isMesh) {
              // item.renderOrder = 2;
              // order --
              item.material = new THREE.MeshBasicMaterial({
                transparent: true,
                opacity: 0,
              });

              if (item.name.toLowerCase().includes('stroke_')) {
                  item.material = new THREE.MeshBasicMaterial();
                  item.visible = false;
            }

              if (item.name.includes("mask")) {
                item.renderOrder = 1;
                item.material.colorWrite = false;
              } 
            }

            if (item.name === "Camera_position") {

              camera.position.copy(
                item.position.multiplyScalar(0.01)
              );
              camera.updateProjectionMatrix();
            }

          });

          resolve(fbxScene);
        },
        (data) => null,
        (error) => null
      );
    });


  }

}

export default new SceneHelper();
